import { Injectable, OnDestroy } from '@angular/core';
import { EfficiencyAnalysisDatastoreService } from '@app/efficiency-analysis/services/efficiency-analysis-datastore.service';
import { ConsumersService } from '@app/shared/services/api/consumers.service';
import { ProducersService } from '@app/shared/services/api/producer.service';
import { AuthService } from '@app/shared/services/auth.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { formatBuildingAddress } from '@app/shared/utility';
import { assign, isNil, set } from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { filter as rxjsFilter, map, takeUntil, first } from 'rxjs/operators';
import { MapStoreService } from './map-store.service';

@Injectable({
  providedIn: 'root',
})
export class BuildingsStoreService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private selectedBuildingStore = new BehaviorSubject<any>(null);

  private selectedBuildingWithUnkownTypeOrGpsStore = new BehaviorSubject<any>(null);

  private selectingBuildingWithUnkownTypeOrGpsStore = new BehaviorSubject<any>(null);

  private buildingsStore: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  private connectionObjectNumbersStore = new BehaviorSubject<any>(null);

  public readonly buildings$ = this.buildingsStore.asObservable();

  public readonly buildings = this.buildingsStore.getValue();

  public readonly selectedBuilding$ = this.selectedBuildingStore.asObservable();

  public readonly selectedBuildingWithUnkownTypeOrGps$ = this.selectedBuildingWithUnkownTypeOrGpsStore.asObservable();

  public readonly selectingBuildingWithUnkownTypeOrGps$ = this.selectingBuildingWithUnkownTypeOrGpsStore.asObservable();

  public readonly connectionObjectNumbers$ = this.connectionObjectNumbersStore.asObservable();

  public get selectedBuilding() {
    return this.selectedBuildingStore.value;
  }

  public set selectedBuilding(value) {
    this.selectedBuildingStore.next(value);
  }

  public get connectionObjectNumbers() {
    return this.connectionObjectNumbersStore.getValue();
  }

  public set selectedBuildingWithUnkownTypeOrGps(value: any) {
    if (this.selectedBuilding) {
      this.selectedBuildingWithUnkownTypeOrGpsStore.next(null);
      this.selectingBuildingWithUnkownTypeOrGpsStore.next(value);
    } else {
      this.selectedBuildingWithUnkownTypeOrGpsStore.next(value);
      this.selectingBuildingWithUnkownTypeOrGpsStore.next(null);
    }
  }

  constructor(
    private mapStoreService: MapStoreService,
    public consumersService: ConsumersService,
    public producersService: ProducersService,
    public authService: AuthService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private efficiencyAnalysisDataStore: EfficiencyAnalysisDatastoreService
  ) {
    mapStoreService.consumerMarkers$
      .pipe(
        rxjsFilter((buildings) => !!buildings),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((buildings: any[]) => this.buildingsStore.next(buildings));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  buildingsWithNoCoordinatesAndType = this.buildings$.pipe(
    map((x) => this.filterBuildingsWithNoCoordinatesAndType(x)),
    map((buildings) => this.mapBuildingStatus(buildings, 'nogpsandtype')),
    map((x) => this.mapBuildingsMeters(x))
  );

  buildingsWithNoCoordinates = this.buildings$.pipe(
    map((x) => this.filterBuildingsWithNoCoordinates(x)),
    map((buildings) => this.mapBuildingStatus(buildings, 'nogps')),
    map((x) => this.mapBuildingsMeters(x))
  );

  buildingsWithNoType = this.buildings$.pipe(
    map((x) => this.filterBuildingsWithNoType(x)),
    map((buildings) => this.mapBuildingStatus(buildings, 'notype')),
    map((x) => this.mapBuildingsMeters(x))
  );

  buildingsWithNoCoordinatesOrType = combineLatest([this.buildingsWithNoCoordinatesAndType, this.buildingsWithNoCoordinates, this.buildingsWithNoType]).pipe(
    map(([x, y, z]) => [...x, ...y, ...z])
  );

  buildingsWithNoCoordinatesOrTypeCount = this.buildingsWithNoCoordinatesOrType.pipe(map((x: any) => (x.length ? x.length : 0)));

  buildingsWithNoCoordinatesOrTypeConnectionObjectNumbers = this.buildingsWithNoCoordinatesOrType.pipe(
    map((x: any) => x.map((y: any) => y.marker.options.data.connectionObjectNumber))
  );

  public setConnectionObjectNumbers = (value: any) => this.connectionObjectNumbersStore.next(value);

  filterBuildingsWithNoCoordinatesAndType = (buildings: any) => buildings.filter(this.isBuildingsWithNoCoordinatesAndType);

  filterBuildingsWithNoCoordinates = (buildings: any) => buildings.filter(this.isBuildingsWithNoCoordinates);

  filterBuildingsWithNoType = (buildings: any) => buildings.filter(this.isBuildingsWithNoType);

  isBuildingsWithNoCoordinatesAndType = (building: any) =>
    (isNil(building.options.data.longitude) || isNil(building.options.data.latitude)) && isNil(building.options.data.buildingTypeId);

  isBuildingsWithNoCoordinates = (building: any) =>
    (isNil(building.options.data.longitude) || isNil(building.options.data.latitude)) && !isNil(building.options.data.buildingTypeId);

  isBuildingsWithNoType = (building: any) =>
    (!isNil(building.options.data.longitude) || !isNil(building.options.data.latitude)) && isNil(building.options.data.buildingTypeId);

  mapBuildingStatus = (buildings: any, status: any) => buildings.map((building: any) => ({ marker: building, status: status }));

  setSelectedBuildingWithUnkownTypeOrGpsStore = (building: any) => this.selectedBuildingWithUnkownTypeOrGpsStore.next(building);

  updateBuilding = (building: any) =>
    this.buildings$
      .pipe(
        map((buildings) => this.removeBuildingMarker(buildings, building)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

  removeBuildingMarker = (buildings: any, buildingToBeRemoved: any) =>
    buildings.filter((building: any) => building.options.data.guid !== buildingToBeRemoved.options.data.guid);

  addBuildingmarker = (buildings: any, buildingToBeAdded: any) => buildings.push(buildingToBeAdded);

  setSelectedBuildingStore = (selectedBuilding: any) => this.selectedBuildingStore.next(selectedBuilding);

  saveBuilding = (updateBuilding: any) => {
    const building = updateBuilding.marker.options.data;
    if (building.type === 'consumer') {
      const newConsumer = {
        guid: building.guid,
        name: building.name,
        buildingTypeId: !isNil(building.buildingType) ? building.buildingType.buildingTypeId : null,
        description: building.description,
        modifyUser: this.authService.currentUser.guid,
        modifyDate: moment.utc().toDate(),
        longitude: building.longitude,
        latitude: building.latitude,
        gpsModifiedBy: this.authService.currentUser.guid,
        gpsModifyDate: moment.utc().toDate(),
        municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
      };

      this.consumersService
        .updateBuilding(newConsumer)
        .pipe(first())
        .subscribe(() => {
          set(
            building,
            'marker.options.data',
            assign(building.marker.options.data, newConsumer, {
              buildingType: !isNil(building.buildingType) ? building.buildingType : null,
            })
          );
          const updatedMarkers = this.removeBuildingFromList(building, this.buildingsStore.getValue());
          updatedMarkers.push(updateBuilding.marker);
          this.mapStoreService.updateConsumerMarker(updatedMarkers);
          this.efficiencyAnalysisDataStore.loadBuildingEfficiencyAnalysis().pipe(first()).subscribe();
        });
    } else if (building.type === 'producer') {
      const newProducer = {
        name: building.name,
        unitPerformance: building.unitPerformance,
        description: building.description,
        modifyUser: this.authService.currentUser.guid,
        modifyDate: moment.utc().toDate(),
      };
      this.producersService
        .updateDetails(building.assetId, newProducer)
        .pipe(first())
        .subscribe(() => {
          set(building, 'marker.options.data', assign(building.marker.options.data, newProducer));
        });
    }
  };

  updateBuildingLatLng = (building: any) => {
    if (building.type === 'consumer') {
      const newConsumer = {
        guid: building.guid,
        modifyUser: this.authService.currentUser.guid,
        modifyDate: moment.utc().toDate(),
        longitude: building.longitude,
        latitude: building.latitude,
        gpsModifiedBy: this.authService.currentUser.guid,
        gpsModifyDate: moment.utc().toDate(),
        municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
      };

      this.consumersService
        .updateBuilding(newConsumer)
        .pipe(first())
        .subscribe(() => {
          set(
            building,
            'marker.options.data',
            assign(building.marker.options.data, newConsumer, {
              buildingType: !isNil(building.buildingType) ? building.buildingType : null,
            })
          );
          const updatedMarkers = this.removeBuildingFromList(building, this.buildingsStore.getValue());
          updatedMarkers.push(building.marker);
          this.mapStoreService.updateConsumerMarker(updatedMarkers);
        });
    }
  };

  private removeBuildingFromList = (building: any, buildings: any) => buildings.filter((x: any) => x.options.data.guid !== building.guid);

  private mapBuildingsMeters = (buildings: any) => {
    return buildings.map((building: any) => {
      building.marker.options.data.fullAddress = formatBuildingAddress(building.marker.options.data);
      return building;
    });
  };

  updateBuildingType = (building: any, buildingType: any) =>
    assign(building, {
      buildingType: {
        name: buildingType.name,
        buildingTypeId: buildingType.guid,
        imageName: buildingType.imageName,
        targetEfficency: buildingType.targetProficiency,
      },
    });
}
